import { useEffect, useRef } from 'react';

const vertexShader = `
  attribute vec2 position;
  void main() {
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;
  uniform float u_time;
  uniform vec2 u_resolution;

  // Simplex-style noise
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  // Soft FBM — fewer octaves, very smooth
  float fbm(vec3 p) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;
    for (int i = 0; i < 3; i++) {
      value += amplitude * snoise(p * frequency);
      amplitude *= 0.45;
      frequency *= 1.8;
    }
    return value;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float aspect = u_resolution.x / u_resolution.y;
    vec2 uvAspect = vec2(uv.x * aspect, uv.y);

    float t = u_time * 0.3;

    // Very slow, large-scale warped flow — cloudy water feel
    vec3 p = vec3(uvAspect * 0.6, t * 0.0192);

    float warp = fbm(p + vec3(
      snoise(p + vec3(0.0, 0.0, t * 0.0096)) * 0.4,
      snoise(p + vec3(5.2, 1.3, t * 0.008)) * 0.4,
      0.0
    ));

    float flow = fbm(p + vec3(
      warp * 0.8 + t * 0.0064,
      warp * 0.6 - t * 0.0048,
      warp * 0.3
    ));

    // Soft feathery wisps — very stretched, very subtle
    vec2 stretched = vec2(uvAspect.x + uvAspect.y * 0.3, uvAspect.y * 2.0);
    float wisps = snoise(vec3(stretched * 0.8, t * 0.0128)) * 0.3;

    // Large dreamy gradient
    float sky = snoise(vec3(uvAspect * 0.2, t * 0.0064)) * 0.5 + 0.5;

    // Combine very gently
    float combined = flow * 0.4 + wisps * 0.2 + sky * 0.4;

    // Muted blue palette — R = G so the hue sits squarely on neutral blue,
    // neither cyan (G > R) nor purple (R > G).
    vec3 deepBlue    = vec3(0.78, 0.84, 0.88);   // hair of cyan
    vec3 midBlue     = vec3(0.90, 0.95, 0.97);
    vec3 brightBlue  = vec3(0.98, 1.00, 1.00);
    vec3 paleBlue    = vec3(1.00, 1.00, 1.00);
    vec3 softDeep    = vec3(0.62, 0.72, 0.78);   // strongest accent, still barely there

    // Off-white alternative — uncomment to swap:
    // vec3 deepBlue    = vec3(0.84, 0.83, 0.82);
    // vec3 midBlue     = vec3(0.95, 0.95, 0.94);
    // vec3 brightBlue  = vec3(0.97, 0.97, 0.96);
    // vec3 paleBlue    = vec3(0.98, 0.98, 0.97);
    // vec3 softDeep    = vec3(0.68, 0.66, 0.63);

    // Mostly bright with dark accents — bias toward brighter bands
    vec3 color;
    float t1 = smoothstep(-0.4, -0.05, combined);
    float t2 = smoothstep(-0.15, 0.2, combined);
    float t3 = smoothstep(0.05, 0.45, combined);

    color = mix(softDeep, deepBlue, t1);
    color = mix(color, midBlue, t2);
    color = mix(color, brightBlue, t3);
    color = mix(color, paleBlue, t3);

    gl_FragColor = vec4(color, 1.0);
  }
`;

export function HeroBackground({ className, pauseAfter, speed = 1, timeOffset = 0 }: { className?: string; pauseAfter?: number; speed?: number; timeOffset?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
    if (!gl) return;

    // Compile shaders
    function createShader(gl: WebGLRenderingContext, type: number, source: string) {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vs = createShader(gl, gl.VERTEX_SHADER, vertexShader);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader);
    if (!vs || !fs) return;

    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program error:', gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    // Full-screen quad
    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const positionLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

    const timeLoc = gl.getUniformLocation(program, 'u_time');
    const resolutionLoc = gl.getUniformLocation(program, 'u_resolution');

    // Render at reduced resolution for performance
    const SCALE = 0.5;

    function resize() {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const w = Math.floor(rect.width * SCALE);
      const h = Math.floor(rect.height * SCALE);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl!.viewport(0, 0, w, h);
      }
    }

    const startTime = performance.now();
    let isVisible = true;

    function render() {
      resize();
      const realElapsed = (performance.now() - startTime) / 1000 + timeOffset;
      const scaled = realElapsed * speed;
      const elapsed = (pauseAfter ? Math.min(scaled, pauseAfter) : scaled) + 5.0;
      gl!.uniform1f(timeLoc, elapsed);
      gl!.uniform2f(resolutionLoc, canvas!.width, canvas!.height);
      gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);
      if (isVisible && (!pauseAfter || realElapsed < pauseAfter)) {
        animationRef.current = requestAnimationFrame(render);
      } else {
        animationRef.current = 0;
      }
    }

    render();

    const io = new IntersectionObserver(
      (entries) => {
        const nowVisible = entries[0]?.isIntersecting ?? false;
        if (nowVisible === isVisible) return;
        isVisible = nowVisible;
        if (isVisible && !animationRef.current) {
          animationRef.current = requestAnimationFrame(render);
        }
      },
      { rootMargin: '100px 0px' },
    );
    io.observe(canvas);

    return () => {
      io.disconnect();
      cancelAnimationFrame(animationRef.current);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ imageRendering: 'auto' }}
    />
  );
}
